import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { UploadAboutImageAction } from "../../../../../store/action";
import { exportBrandingImage, loadBrandingImage, validateImageFile } from "../../../../../utils/brandingImage";
import { aboutUploadSrc, safeAboutUrl } from "../../../../../utils/aboutContent";
import { btnSecondaryClass, formControlClass } from "../../../dashboardStyles";

/**
 * The picture of an image-and-text block: chosen, optimised in the browser and
 * uploaded at once (About images are stored as they are added, like before),
 * with the description a screen reader reads in its place.
 *
 * The upload holds the form while it runs (`onBusyChange`): saving the section
 * before the image has an address would save it without one.
 */
const AboutImageField = ({ image = null, onChange, onBusyChange, disabled = false }) => {
  const { t } = useTranslation();
  const ids = useId();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    // Reset at once: choosing the same file again must fire a change.
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    let url;
    try {
      validateImageFile(file);
      url = URL.createObjectURL(file);
      const source = await loadBrandingImage(url);
      const optimized = await exportBrandingImage(source, { kind: "header", crop: false });
      const result = await UploadAboutImageAction(optimized);
      const src = safeAboutUrl(aboutUploadSrc(result.url), true);
      if (!src) throw new Error("url");
      onChange({ src, alt: image?.alt || "" });
    } catch {
      setError(t("Dashboard.AboutEditor.uploadError"));
    } finally {
      if (url) URL.revokeObjectURL(url);
      setBusy(false);
    }
  };

  const src = image?.src ? safeAboutUrl(image.src, true) : null;

  return (
    <div className="min-w-0">
      <span className="mb-1.5 block text-sm font-semibold text-ink-800">{t("Dashboard.AboutEditor.image")}</span>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="relative flex aspect-[4/3] w-full shrink-0 items-center justify-center overflow-hidden rounded-xl border border-ink-200 bg-ink-50 sm:w-36">
          {src ? (
            <img src={src} alt="" className="size-full object-cover" />
          ) : (
            <i className="bi bi-image text-2xl text-ink-400" aria-hidden="true" />
          )}
          {busy && (
            <span role="status" className="absolute inset-0 flex items-center justify-center bg-white/80 text-xs font-semibold text-ink-600">
              {t("Dashboard.AboutEditor.uploading")}
            </span>
          )}
          {src && !busy && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(null)}
              aria-label={t("Dashboard.AboutEditor.removeImage")}
              className="absolute top-1.5 right-1.5 flex size-11 cursor-pointer items-center justify-center rounded-full bg-white/90 text-red-600 shadow-card transition hover:bg-white focus-visible:outline-2 focus-visible:outline-brand-600"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <button
              type="button"
              className={btnSecondaryClass}
              disabled={disabled || busy}
              onClick={() => inputRef.current.click()}
            >
              <i className={`bi ${src ? "bi-arrow-repeat" : "bi-upload"}`} aria-hidden="true" />
              {t(src ? "Dashboard.AboutEditor.replaceImage" : "Dashboard.AboutEditor.chooseImage")}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              aria-label={t("Dashboard.AboutEditor.chooseImage")}
              className="sr-only"
              disabled={disabled || busy}
              onChange={upload}
            />
          </div>
          {src && (
            <div className="min-w-0">
              <label htmlFor={`${ids}-alt`} className="mb-1.5 block text-sm font-semibold text-ink-800">
                {t("Dashboard.AboutEditor.alt")}
              </label>
              <input
                id={`${ids}-alt`}
                type="text"
                maxLength={300}
                value={image.alt || ""}
                disabled={disabled}
                onChange={(event) => onChange({ ...image, alt: event.target.value })}
                className={formControlClass}
              />
            </div>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-2 mb-0 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

AboutImageField.propTypes = {
  image: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  onBusyChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default AboutImageField;
